import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'

const ForgotPasswordPage = () => {
  const { t } = useTranslation('auth')
  const [searchParams] = useSearchParams()
  const [step, setStep] = useState(1) // 1 = enter email, 2 = enter code + password
  const [email, setEmail] = useState(searchParams.get('email') || '')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [autoSubmitted, setAutoSubmitted] = useState(false)
  const [codeSent, setCodeSent] = useState(false)

  const { forgotPassword, confirmForgotPassword } = useAuth()
  const navigate = useNavigate()

  const isSetPasswordFlow = window.location.pathname === '/set-password'

  // Auto-submit if email provided via URL (invitation flow)
  useEffect(() => {
    if (isSetPasswordFlow && email && step === 1 && !autoSubmitted) {
      setAutoSubmitted(true)
      handleRequestCode()
    }
  }, [email, step, isSetPasswordFlow, autoSubmitted])

  const handleRequestCode = async (e) => {
    e?.preventDefault()
    setError('')
    setLoading(true)

    const result = await forgotPassword(email)

    if (result.success) {
      setCodeSent(true)
      setStep(2)
    } else {
      setError(result.error || t('errors.failedToSendCode'))
    }

    setLoading(false)
  }

  const handleResetPassword = async (e) => {
    e.preventDefault()
    setError('')

    if (newPassword !== confirmPassword) {
      setError(t('errors.passwordMismatch'))
      return
    }

    if (newPassword.length < 8) {
      setError(t('errors.passwordTooShort'))
      return
    }

    // Check for at least one uppercase, one lowercase, and one digit
    if (!/[A-Z]/.test(newPassword)) {
      setError(t('errors.passwordNeedsUppercase'))
      return
    }
    if (!/[a-z]/.test(newPassword)) {
      setError(t('errors.passwordNeedsLowercase'))
      return
    }
    if (!/[0-9]/.test(newPassword)) {
      setError(t('errors.passwordNeedsNumber'))
      return
    }

    setLoading(true)

    const result = await confirmForgotPassword(email, code, newPassword)

    if (result.success) {
      navigate('/login', {
        state: { message: t('forgotPassword.passwordSetSuccess') }
      })
    } else {
      setError(result.error || t('errors.failedToResetPassword'))
    }

    setLoading(false)
  }

  const title = isSetPasswordFlow ? t('forgotPassword.setPasswordTitle') : t('forgotPassword.title')

  // Dynamic subtitle based on step and state
  const getSubtitle = () => {
    if (step === 1) {
      if (loading && isSetPasswordFlow) {
        return t('forgotPassword.welcomeSendingCode')
      }
      return isSetPasswordFlow
        ? t('forgotPassword.welcomeSetPassword')
        : t('forgotPassword.enterEmailInstructions')
    }
    if (codeSent) {
      return t('forgotPassword.codeSentTo', { email })
    }
    return t('forgotPassword.enterCodeInstructions')
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      padding: '1rem'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        padding: '2rem',
        border: '1px solid #ccc',
        borderRadius: '8px'
      }}>
        <h1 style={{ marginBottom: '0.5rem', textAlign: 'center' }}>{title}</h1>
        <p style={{ marginBottom: '2rem', textAlign: 'center', color: '#666', fontSize: '0.9rem' }}>
          {getSubtitle()}
        </p>

        {error && (
          <div style={{
            padding: '0.75rem',
            marginBottom: '1rem',
            backgroundColor: '#fee',
            color: '#c00',
            borderRadius: '4px'
          }}>
            {error}
          </div>
        )}

        {codeSent && step === 2 && (
          <div style={{
            padding: '0.75rem',
            marginBottom: '1rem',
            backgroundColor: '#d4edda',
            color: '#155724',
            borderRadius: '4px',
            border: '1px solid #c3e6cb'
          }}>
            ✓ {t('forgotPassword.verificationCodeSent')}
          </div>
        )}

        {step === 1 ? (
          <form onSubmit={handleRequestCode}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                {t('forgotPassword.emailLabel')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{ width: '100%' }}
                disabled={loading || (isSetPasswordFlow && searchParams.get('email'))}
                autoFocus={!isSetPasswordFlow}
              />
            </div>

            <button
              type="submit"
              style={{ width: '100%', marginBottom: '1rem' }}
              disabled={loading}
            >
              {loading ? t('forgotPassword.sending') : t('forgotPassword.sendVerificationCode')}
            </button>

            <div style={{ textAlign: 'center' }}>
              <Link to="/login">{t('forgotPassword.backToLogin')}</Link>
            </div>
          </form>
        ) : (
          <form onSubmit={handleResetPassword}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                {t('forgotPassword.verificationCode')}
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                style={{ width: '100%', letterSpacing: '0.2em', textAlign: 'center' }}
                disabled={loading}
                placeholder={t('forgotPassword.enterSixDigitCode')}
                autoFocus
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                {t('forgotPassword.newPassword')}
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                style={{ width: '100%' }}
                disabled={loading}
              />
              <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                {t('forgotPassword.passwordRequirements')}
              </p>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                {t('forgotPassword.confirmPassword')}
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                style={{ width: '100%' }}
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              style={{ width: '100%', marginBottom: '1rem' }}
              disabled={loading}
            >
              {loading ? t('forgotPassword.settingPassword') : t('forgotPassword.setPassword')}
            </button>

            <div style={{ textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => {
                  setStep(1)
                  setCode('')
                  setNewPassword('')
                  setConfirmPassword('')
                  setError('')
                  setCodeSent(false)
                  setAutoSubmitted(false)
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#0066cc',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                {t('forgotPassword.resendCode')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default ForgotPasswordPage
